'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/store/hooks'
import {
    createDepartment,
    updateDepartment,
    getDepartment,
    getDepartmentsByOrganization,
    getDepartmentMembers,
    setDepartmentMembers,
    DepartmentMember,
} from '@/lib/api/atendimento/departments'
import {
    getOrganizationUsers,
    OrganizationUser,
} from '@/lib/api/users/users-api'
import { usePermission } from '@/lib/hooks/use-permission'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import {
    MdArrowBack,
    MdSchedule,
    MdPhone,
    MdBadge,
    MdBusiness,
    MdNotificationsActive,
    MdTrendingUp,
    MdTune,
    MdGroup,
    MdPersonOff,
    MdDelete,
    MdLock,
    MdSpeed,
    MdCallSplit,
} from 'react-icons/md'
import type {
    Department,
    DepartmentAiResumeMode,
} from '@/lib/api/common/interfaces/department.interface'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { getWhatsappChannelReadiness } from '@/lib/api/messaging/whatsapp-templates-api'
import { TEMPLATE_USAGE_SCOPES } from '@/lib/api/messaging/whatsapp-template-scopes'
import { FormField, FormSection } from '@/components/form-section'
import { IgnoredContactsTab } from './ignored-contacts-tab'
import WhatsappChannelSection, {
    WhatsappProviderKind,
} from './whatsapp-channel-section'

const AI_RESUME_MODE_OPTIONS: {
    value: DepartmentAiResumeMode
    title: string
    description: string
}[] = [
    {
        value: 'BACKUP',
        title: 'A IA segura o contato',
        description:
            'Responde curto, sem recomeçar o atendimento, e avisa que a pessoa da equipe dá continuidade. A conversa continua com a equipe.',
    },
    {
        value: 'TAKEOVER',
        title: 'A IA assume a conversa',
        description:
            'A pausa acaba e a IA atende como se a conversa fosse dela. Para setores atendidos só pela IA.',
    },
]

export default function DepartmentsFormPage() {
    const { user, isPartner } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(false)
    // Ao EDITAR, o motivo mais comum de abrir um setor é mexer na conexão do
    // WhatsApp (parear, reconectar, conferir status) — os dados cadastrais
    // mudam raramente. No cadastro novo não há sessão nenhuma, então lá a aba
    // geral continua sendo a primeira.
    const [activeTab, setActiveTab] = useState<
        'geral' | 'whatsapp' | 'membros' | 'ignorados'
    >(isEditing ? 'whatsapp' : 'geral')
    const { canUpdate } = usePermission()
    // Só quem edita setores mexe na composição: trocar membro muda quem lê as
    // conversas, não é ajuste cosmético.
    const canEditMembers = canUpdate('atendimento', 'sectors')
    const [members, setMembers] = useState<DepartmentMember[]>([])
    const [loadingMembers, setLoadingMembers] = useState(false)
    const [savingMembers, setSavingMembers] = useState(false)
    // Candidatos vêm inteiros, sem paginação, e a busca é local.
    const [eligibleUsers, setEligibleUsers] = useState<OrganizationUser[]>([])
    const [userSearch, setUserSearch] = useState('')
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [savingChannel, setSavingChannel] = useState(false)
    const [loadingDepartment, setLoadingDepartment] = useState(false)
    // Saída do WhatsApp Web: confirmação com o que vai parar e o número novo.
    const [showChannelSwitchModal, setShowChannelSwitchModal] = useState(false)
    const [officialApiNumber, setOfficialApiNumber] = useState('')
    const [missingScopes, setMissingScopes] = useState<string[]>([])
    const [loadingScopes, setLoadingScopes] = useState(false)

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        workCalendar: '',
        whatsappNumber: '',
        whatsappProvider: 'WHATSAPP_API' as WhatsappProviderKind,
        isDefaultForNotifications: false,
        createsSalesOpportunities: false,
        aiResumeMode: 'BACKUP' as DepartmentAiResumeMode,
        // Vazio = padrão do produto (30 por hora, 200 por dia).
        flowSendLimitPerHour: '',
        flowSendLimitPerDay: '',
        // Vazio = o próprio setor atende o que chega no número dele.
        routeToDepartmentId: '',
    })
    const [organizationDepartments, setOrganizationDepartments] = useState<
        Department[]
    >([])

    // Setores da organização, para o roteamento de mensagens.
    useEffect(() => {
        const organizationId =
            user?.organizationId ?? user?.company?.organizationId ?? null
        if (!organizationId) return
        getDepartmentsByOrganization(organizationId).then(res => {
            if (res.success && Array.isArray(res.result)) {
                setOrganizationDepartments(res.result)
            }
        })
    }, [user?.organizationId, user?.company?.organizationId])

    const currentDepartmentId = id ? parseInt(id, 10) : null
    // Um salto só: quem já roteia não pode ser destino, e quem é destino não pode rotear.
    const routeTargets = organizationDepartments.filter(
        d => d.id !== currentDepartmentId && !d.routeToDepartmentId
    )
    const routedFromDepartments = organizationDepartments.filter(
        d =>
            currentDepartmentId != null &&
            d.routeToDepartmentId === currentDepartmentId
    )
    const selectedRouteTarget = organizationDepartments.find(
        d => String(d.id) === formData.routeToDepartmentId
    )

    useEffect(() => {
        if (isEditing && id) {
            loadDepartment()
        }
    }, [id, isEditing])

    // Carrega só quando a aba abre: a maioria das visitas ao setor é para
    // mexer no WhatsApp, e a lista de membros não é barata.
    useEffect(() => {
        if (activeTab !== 'membros' || !id) return

        let cancelled = false
        setLoadingMembers(true)
        const departmentId = parseInt(id, 10)

        Promise.all([
            getDepartmentMembers(departmentId),
            getOrganizationUsers(),
        ])
            .then(([membersResponse, eligibleResponse]) => {
                if (cancelled) return
                if (membersResponse.success && Array.isArray(membersResponse.result)) {
                    setMembers(membersResponse.result)
                } else {
                    toast.error(
                        membersResponse.error || 'Erro ao carregar os membros'
                    )
                }
                if (eligibleResponse.success && Array.isArray(eligibleResponse.result)) {
                    setEligibleUsers(eligibleResponse.result)
                } else {
                    // Falhar aqui em silêncio é pior que falhar alto: a lista
                    // fica vazia e a tela diz "todos já são membros", o que
                    // parece uma resposta legítima. Já custou um diagnóstico.
                    setEligibleUsers([])
                    toast.error(
                        eligibleResponse.error ||
                            'Não foi possível carregar os usuários para adicionar'
                    )
                }
            })
            .catch(() => {
                if (!cancelled) toast.error('Erro ao carregar os membros')
            })
            .finally(() => {
                if (!cancelled) setLoadingMembers(false)
            })

        return () => {
            cancelled = true
        }
    }, [activeTab, id])

    /**
     * Grava a composição inteira de uma vez. O backend recebe a lista final e
     * resolve o que criar, reativar e desativar — não há "adicionar" e
     * "remover" separados, para não abrir espaço para estado meio-salvo.
     */
    const saveMembers = async (userIds: number[]) => {
        if (!id) return

        setSavingMembers(true)
        try {
            const response = await setDepartmentMembers(
                parseInt(id, 10),
                userIds
            )
            if (response.success && Array.isArray(response.result)) {
                setMembers(response.result)
            } else {
                toast.error(response.error || 'Erro ao salvar os membros')
            }
        } catch {
            toast.error('Erro ao salvar os membros')
        } finally {
            setSavingMembers(false)
        }
    }

    /** Quem ainda não é membro, filtrado pela busca local. */
    const availableUsers = eligibleUsers.filter(user => {
        if (members.some(m => m.userId === user.id)) return false
        const term = userSearch.trim().toLowerCase()
        if (!term) return true
        return (
            user.name.toLowerCase().includes(term) ||
            (user.email || '').toLowerCase().includes(term) ||
            user.companyNames.some(company => company.toLowerCase().includes(term))
        )
    })

    const toggleUser = (userId: number) => {
        setSelectedUserIds(previous =>
            previous.includes(userId)
                ? previous.filter(id => id !== userId)
                : [...previous, userId]
        )
    }

    const toggleAllUsers = () => {
        setSelectedUserIds(previous =>
            previous.length === availableUsers.length
                ? []
                : availableUsers.map(user => user.id)
        )
    }

    const addSelectedMembers = async () => {
        if (selectedUserIds.length === 0) return
        await saveMembers([...members.map(m => m.userId), ...selectedUserIds])
        setSelectedUserIds([])
        setUserSearch('')
    }

    const removeMember = (userId: number) => {
        saveMembers(members.map(m => m.userId).filter(uid => uid !== userId))
    }

    const loadDepartment = async () => {
        if (!id) return

        setLoadingDepartment(true)
        try {
            const response = await getDepartment(parseInt(id, 10))
            if (response.success) {
                const department = Array.isArray(response.result)
                    ? response.result[0]
                    : response.result
                if (department) {
                    setFormData({
                        name: department.name,
                        description: department.description || '',
                        workCalendar: department.workCalendar || '',
                        whatsappNumber:
                            (department as any).whatsappNumber || '',
                        whatsappProvider:
                            ((department as any)
                                .whatsappProvider as WhatsappProviderKind) ||
                            'WHATSAPP_API',
                        isDefaultForNotifications:
                            (department as any).isDefaultForNotifications ??
                            false,
                        createsSalesOpportunities:
                            (department as any).createsSalesOpportunities ??
                            false,
                        aiResumeMode: department.aiResumeMode ?? 'BACKUP',
                        flowSendLimitPerHour:
                            department.flowSendLimitPerHour?.toString() ?? '',
                        flowSendLimitPerDay:
                            department.flowSendLimitPerDay?.toString() ?? '',
                        routeToDepartmentId:
                            department.routeToDepartmentId?.toString() ?? '',
                    })
                } else {
                    toast.error('Setor não encontrado')
                    navigate('/atendimento/sectors')
                }
            } else {
                toast.error(response.error || 'Erro ao carregar o setor')
                navigate('/atendimento/sectors')
            }
        } catch (error) {
            toast.error('Erro ao carregar o setor')
            navigate('/atendimento/sectors')
        } finally {
            setLoadingDepartment(false)
        }
    }

    /**
     * A troca de canal grava na hora, sem esperar o "Salvar".
     *
     * Conectar por QR code é uma ação do backend contra o setor JÁ gravado: se o
     * canal só fosse persistido no submit, o botão Conectar bateria num setor
     * ainda no canal oficial e seria recusado. Em setor novo (sem id) não há o
     * que gravar — o bloco de conexão já orienta a salvar primeiro.
     */
    const changeChannel = async (provider: WhatsappProviderKind) => {
        const previous = formData.whatsappProvider
        if (provider === previous) return

        // Sair do WhatsApp Web não é troca de configuração, é troca de canal com
        // consequências: a sessão pareada é encerrada, a janela de 24h volta e
        // as notificações sem template aprovado param. Além disso o número atual
        // veio do QR e não vale na API oficial. Tudo isso precisa estar na tela
        // ANTES do clique valer — depois, o operador só descobriria pelo que
        // parou de funcionar.
        if (
            previous === 'WHATSAPP_WEB' &&
            provider !== 'WHATSAPP_WEB' &&
            isEditing &&
            id
        ) {
            setOfficialApiNumber('')
            setShowChannelSwitchModal(true)
            void loadMissingScopes()
            return
        }

        setFormData(prev => ({ ...prev, whatsappProvider: provider }))

        if (!isEditing || !id) return

        setSavingChannel(true)
        try {
            const response = await updateDepartment(parseInt(id, 10), {
                whatsappProvider: provider,
            })

            if (!response.success) {
                // Reverte: deixar o botão aceso sem o backend ter aceitado faria
                // o operador tentar conectar e levar erro sem entender por quê.
                setFormData(prev => ({ ...prev, whatsappProvider: previous }))
                toast.error(
                    response.error || 'Não foi possível alterar o canal'
                )
            }
        } catch (error: any) {
            setFormData(prev => ({ ...prev, whatsappProvider: previous }))
            toast.error(
                error?.response?.data?.message ||
                    'Não foi possível alterar o canal'
            )
        } finally {
            setSavingChannel(false)
        }
    }

    const loadMissingScopes = async () => {
        setLoadingScopes(true)
        try {
            const response = await getWhatsappChannelReadiness()
            setMissingScopes(
                response.success ? (response.result?.missingScopes ?? []) : []
            )
        } catch {
            // Não saber quais escopos faltam não pode impedir a troca — o aviso
            // é informativo, a decisão é do operador.
            setMissingScopes([])
        } finally {
            setLoadingScopes(false)
        }
    }

    const confirmChannelSwitch = async () => {
        if (!id) return

        const digits = officialApiNumber.replace(/\D/g, '')
        if (digits.length < 10) {
            toast.warning('Informe o número de WhatsApp da API oficial')
            return
        }

        setSavingChannel(true)
        try {
            const response = await updateDepartment(parseInt(id, 10), {
                whatsappProvider: 'WHATSAPP_API' as WhatsappProviderKind,
                whatsappNumber: digits,
            })

            if (response.success) {
                setFormData(prev => ({
                    ...prev,
                    whatsappProvider: 'WHATSAPP_API' as WhatsappProviderKind,
                    whatsappNumber: digits,
                }))
                setShowChannelSwitchModal(false)
                toast.success(
                    'Canal alterado para a API oficial. A sessão do WhatsApp Web foi encerrada.'
                )
            } else {
                toast.error(
                    response.error || 'Não foi possível alterar o canal'
                )
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    'Não foi possível alterar o canal'
            )
        } finally {
            setSavingChannel(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // organizationId so e obrigatorio na criacao; na edicao o backend nao precisa dele.
        const organizationId =
            user?.organizationId ?? user?.company?.organizationId ?? null

        if (!isEditing && !organizationId) {
            toast.error('Usuário sem organização')
            return
        }

        if (!formData.name.trim()) {
            setActiveTab('geral')
            toast.warning('Preencha o nome do setor')
            return
        }

        if (!formData.description.trim()) {
            setActiveTab('geral')
            toast.warning('Preencha a descrição do setor')
            return
        }

        // No canal WhatsApp Web o número vem do pareamento por QR code — exigi-lo
        // no cadastro obrigaria o usuário a adivinhá-lo antes de conectar.
        if (
            formData.whatsappProvider !== 'WHATSAPP_WEB' &&
            !formData.whatsappNumber.trim()
        ) {
            setActiveTab('whatsapp')
            toast.warning('Preencha o número de WhatsApp')
            return
        }

        setLoading(true)

        try {
            let response

            if (isEditing) {
                response = await updateDepartment(parseInt(id!, 10), {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    workCalendar: formData.workCalendar.trim(),
                    whatsappNumber: formData.whatsappNumber.trim() || undefined,
                    whatsappProvider: formData.whatsappProvider,
                    isDefaultForNotifications:
                        formData.isDefaultForNotifications,
                    createsSalesOpportunities:
                        formData.createsSalesOpportunities,
                    aiResumeMode: formData.aiResumeMode,
                    flowSendLimitPerHour: formData.flowSendLimitPerHour
                        ? Number(formData.flowSendLimitPerHour)
                        : null,
                    flowSendLimitPerDay: formData.flowSendLimitPerDay
                        ? Number(formData.flowSendLimitPerDay)
                        : null,
                    routeToDepartmentId: formData.routeToDepartmentId
                        ? Number(formData.routeToDepartmentId)
                        : null,
                })
            } else {
                response = await createDepartment({
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    workCalendar: formData.workCalendar.trim(),
                    whatsappNumber: formData.whatsappNumber.trim() || undefined,
                    whatsappProvider: formData.whatsappProvider,
                    isDefaultForNotifications:
                        formData.isDefaultForNotifications,
                    createsSalesOpportunities:
                        formData.createsSalesOpportunities,
                    aiResumeMode: formData.aiResumeMode,
                    organizationId: organizationId!,
                    ...(formData.routeToDepartmentId
                        ? {
                              routeToDepartmentId: Number(
                                  formData.routeToDepartmentId
                              ),
                          }
                        : {}),
                    ...(isPartner && user?.partnerId
                        ? { partnerId: user.partnerId }
                        : {}),
                })
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Setor atualizado com sucesso'
                        : 'Setor criado com sucesso'
                )
                navigate('/atendimento/sectors')
            } else {
                toast.error(
                    response.error ||
                        (isEditing
                            ? 'Erro ao atualizar o setor'
                            : 'Erro ao criar o setor')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar o setor'
                    : 'Erro ao criar o setor'
            )
        } finally {
            setLoading(false)
        }
    }

    if (loadingDepartment) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando setor...
                </p>
            </div>
        )
    }

    const isWeb = formData.whatsappProvider === 'WHATSAPP_WEB'

    /* Navegação em trilha lateral, não em abas: as abas antigas ficavam a poucos
       pixels do seletor de canal do WhatsApp — dois controles de aparência igual
       e efeito completamente diferente, empilhados. Aqui a navegação da página
       vive na lateral e nada dentro do conteúdo se parece com ela. */
    const steps = [
        {
            key: 'geral' as const,
            label: 'Dados do setor',
            hint: 'Nome, descrição e horários',
            icon: <MdBadge className="text-xl" />,
        },
        {
            key: 'whatsapp' as const,
            label: 'WhatsApp',
            hint: isWeb ? 'Canal Web (QR code)' : 'Canal API oficial',
            icon: <MdPhone className="text-xl" />,
        },
        // Só no modo edição: sem setor gravado não há a quem vincular membro
        // nem conversa de onde tirar contato.
        ...(isEditing
            ? [
                  {
                      key: 'membros' as const,
                      label: 'Membros',
                      hint: 'Quem enxerga os atendimentos',
                      icon: <MdGroup className="text-xl" />,
                  },
                  {
                      key: 'ignorados' as const,
                      label: 'Ignorados',
                      hint: 'Contatos que a IA não responde',
                      icon: <MdPersonOff className="text-xl" />,
                  },
              ]
            : []),
    ]

    return (
        <div className="flex flex-col gap-6">
            <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-linear-to-r from-secondary/10 via-primary/5 to-transparent"
                />
                <div className="relative flex items-center gap-4 p-5">
                    <button
                        type="button"
                        onClick={() => navigate('/atendimento/sectors')}
                        className="btn btn-ghost btn-circle btn-sm shrink-0"
                        title="Voltar para a lista"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary ring-1 ring-secondary/20">
                        <MdBusiness className="text-2xl" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-base-content/40">
                            {isEditing ? 'Editando setor' : 'Novo cadastro'}
                        </p>
                        <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                            {isEditing
                                ? formData.name || 'Editar setor'
                                : 'Novo setor'}
                        </h1>
                    </div>
                    <span className="hidden items-center gap-2 rounded-full border border-base-300 bg-base-100 px-3 py-1.5 text-xs font-medium text-base-content/70 sm:inline-flex">
                        <MdPhone className="text-base text-base-content/40" />
                        {isWeb ? 'WhatsApp Web' : 'WhatsApp API'}
                    </span>
                </div>
            </header>

            <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[248px_minmax(0,1fr)]"
            >
                <nav
                    aria-label="Seções do cadastro"
                    className="flex gap-2 lg:sticky lg:top-4 lg:flex-col"
                >
                    {steps.map(step => {
                        const isActive = activeTab === step.key
                        return (
                            <button
                                key={step.key}
                                type="button"
                                aria-current={isActive ? 'step' : undefined}
                                onClick={() => setActiveTab(step.key)}
                                className={`flex flex-1 items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors lg:flex-none ${
                                    isActive
                                        ? 'border-secondary/30 bg-secondary/10'
                                        : 'border-base-300 bg-base-100 hover:bg-base-200'
                                }`}
                            >
                                <span
                                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                        isActive
                                            ? 'bg-secondary text-secondary-content'
                                            : 'bg-base-200 text-base-content/40'
                                    }`}
                                >
                                    {step.icon}
                                </span>
                                <span className="min-w-0">
                                    <span
                                        className={`block truncate text-sm font-semibold ${
                                            isActive
                                                ? 'text-base-content'
                                                : 'text-base-content/70'
                                        }`}
                                    >
                                        {step.label}
                                    </span>
                                    <span className="hidden truncate text-xs text-base-content/45 sm:block">
                                        {step.hint}
                                    </span>
                                </span>
                            </button>
                        )
                    })}
                </nav>

                {/* Conteúdo renderiza condicionalmente (e não com display:none) de
                    propósito: campo `required` escondido trava o submit no browser
                    sem mostrar erro, porque ele não consegue focar o que não vê. */}
                <div className="flex min-w-0 flex-col gap-6">
                    {activeTab === 'geral' && (
                        <>
                            <FormSection
                                icon={<MdBadge className="text-lg" />}
                                title="Identificação"
                                subtitle="Como o setor aparece para a equipe"
                            >
                                <FormField
                                    label="Nome"
                                    htmlFor="sector-name"
                                    required
                                >
                                    <input
                                        id="sector-name"
                                        type="text"
                                        placeholder="Ex.: Recepção, Financeiro, Agendamento"
                                        className="input input-bordered w-full"
                                        value={formData.name}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                name: e.target.value,
                                            })
                                        }
                                        required
                                    />
                                </FormField>

                                <FormField
                                    label="Descrição"
                                    htmlFor="sector-description"
                                    required
                                    hint="Explique o que esse setor resolve — ajuda a equipe a transferir o atendimento certo."
                                >
                                    <textarea
                                        id="sector-description"
                                        className="textarea textarea-bordered w-full leading-relaxed"
                                        placeholder="Ex.: Atende dúvidas sobre agendamento, remarcação e cancelamento."
                                        value={formData.description}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                description: e.target.value,
                                            })
                                        }
                                        rows={4}
                                        required
                                    />
                                </FormField>
                            </FormSection>

                            <FormSection
                                icon={<MdTune className="text-lg" />}
                                title="Funcionamento"
                                subtitle="Comportamento do setor no dia a dia"
                            >
                                {/* Toggle dentro de um cartão clicável: solto na
                                    coluna ele se perdia entre os textos de apoio. */}
                                <label
                                    className={`flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-colors ${
                                        formData.isDefaultForNotifications
                                            ? 'border-secondary/30 bg-secondary/5'
                                            : 'border-base-300 bg-base-100 hover:bg-base-200/50'
                                    }`}
                                >
                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                            formData.isDefaultForNotifications
                                                ? 'bg-secondary/15 text-secondary'
                                                : 'bg-base-200 text-base-content/40'
                                        }`}
                                    >
                                        <MdNotificationsActive className="text-lg" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-base-content">
                                            Padrão para notificações
                                        </span>
                                        <span className="mt-1 block text-xs leading-relaxed text-base-content/50">
                                            As notificações automáticas
                                            (lembretes, cobranças) abrem o
                                            atendimento por este setor.
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm mt-0.5 shrink-0"
                                        checked={
                                            formData.isDefaultForNotifications
                                        }
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                isDefaultForNotifications:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </label>

                                {/* Quem responde a um lembrete não é lead: por isso a
                                    escolha é por setor, e não vale para todos. */}
                                <label
                                    className={`flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-colors ${
                                        formData.createsSalesOpportunities
                                            ? 'border-secondary/30 bg-secondary/5'
                                            : 'border-base-300 bg-base-100 hover:bg-base-200/50'
                                    }`}
                                >
                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                            formData.createsSalesOpportunities
                                                ? 'bg-secondary/15 text-secondary'
                                                : 'bg-base-200 text-base-content/40'
                                        }`}
                                    >
                                        <MdTrendingUp className="text-lg" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-base-content">
                                            Gerar oportunidade no funil
                                        </span>
                                        <span className="mt-1 block text-xs leading-relaxed text-base-content/50">
                                            Toda conversa que o contato iniciar
                                            neste setor entra no funil de vendas,
                                            mesmo que o atendimento vá direto
                                            para uma pessoa. Ligue nos setores
                                            comerciais; num setor de avisos, a
                                            resposta a um lembrete viraria
                                            oportunidade à toa.
                                        </span>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm mt-0.5 shrink-0"
                                        checked={formData.createsSalesOpportunities}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                createsSalesOpportunities:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </label>

                                {/* Por setor porque depende de quem atende: num setor só de
                                    IA ela assume; num setor com consultoras, só segura o
                                    contato até a pessoa voltar. */}
                                <FormField
                                    label="Quando o contato fica sem resposta numa conversa da equipe"
                                    hint="Vale depois do prazo definido em Mensageria > Configurações. Sem prazo configurado, a IA não volta sozinha."
                                >
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {AI_RESUME_MODE_OPTIONS.map(option => {
                                            const selected =
                                                formData.aiResumeMode === option.value
                                            return (
                                                <label
                                                    key={option.value}
                                                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                                                        selected
                                                            ? 'border-secondary bg-secondary/5'
                                                            : 'border-base-300 bg-base-100 hover:bg-base-200/50'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="ai-resume-mode"
                                                        className="radio radio-secondary radio-sm mt-0.5"
                                                        checked={selected}
                                                        onChange={() =>
                                                            setFormData({
                                                                ...formData,
                                                                aiResumeMode: option.value,
                                                            })
                                                        }
                                                    />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block text-sm font-medium text-base-content">
                                                            {option.title}
                                                        </span>
                                                        <span className="mt-1 block text-xs leading-relaxed text-base-content/60">
                                                            {option.description}
                                                        </span>
                                                    </span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </FormField>

                                <FormField
                                    label="Horários de funcionamento"
                                    htmlFor="sector-calendar"
                                    icon={<MdSchedule className="text-base" />}
                                    optional
                                    hint="Texto livre, exibido como referência para a equipe."
                                >
                                    <textarea
                                        id="sector-calendar"
                                        className="textarea textarea-bordered w-full leading-relaxed"
                                        placeholder="Ex.: Segunda a sexta: 09:00 - 18:00"
                                        value={formData.workCalendar}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                workCalendar: e.target.value,
                                            })
                                        }
                                        rows={3}
                                    />
                                </FormField>
                            </FormSection>
                        </>
                    )}

                    {activeTab === 'whatsapp' && (
                        <FormSection
                            icon={<MdPhone className="text-lg" />}
                            title="WhatsApp do setor"
                            subtitle="Por onde as mensagens entram e saem"
                        >
                            <WhatsappChannelSection
                                departmentId={
                                    isEditing && id ? parseInt(id, 10) : null
                                }
                                provider={formData.whatsappProvider}
                                onProviderChange={changeChannel}
                                savingChannel={savingChannel}
                            />

                            {!isWeb ? (
                                <FormField
                                    label="Número WhatsApp"
                                    htmlFor="sector-whatsapp"
                                    icon={<MdPhone className="text-base" />}
                                    required
                                    hint="Mensagens recebidas nesse número caem direto neste setor."
                                >
                                    <input
                                        id="sector-whatsapp"
                                        type="text"
                                        placeholder="+55 11 99999-9999"
                                        className="input input-bordered w-full sm:max-w-xs"
                                        value={formData.whatsappNumber}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                whatsappNumber:
                                                    formatPhoneNumber(
                                                        e.target.value
                                                    ),
                                            })
                                        }
                                    />
                                </FormField>
                            ) : (
                                formData.whatsappNumber && (
                                    <FormField
                                        label="Número WhatsApp"
                                        htmlFor="sector-whatsapp-readonly"
                                        icon={<MdPhone className="text-base" />}
                                        hint="Preenchido automaticamente pelo pareamento por QR code."
                                    >
                                        <input
                                            id="sector-whatsapp-readonly"
                                            type="text"
                                            className="input input-bordered w-full sm:max-w-xs"
                                            value={formData.whatsappNumber}
                                            disabled
                                        />
                                    </FormField>
                                )
                            )}

                            <FormField
                                label="Roteamento de mensagens"
                                htmlFor="sector-route-to"
                                icon={<MdCallSplit className="text-base" />}
                                optional
                                hint={
                                    selectedRouteTarget
                                        ? `Quem escrever para o número deste setor será atendido por "${selectedRouteTarget.name}": fila, agente de IA e atendentes de lá. As respostas continuam saindo pelo número deste setor, o mesmo em que o cliente escreveu.`
                                        : 'Mensagens recebidas no número deste setor são atendidas por ele mesmo. Escolha outro setor para que ele atenda essas conversas; as respostas continuam saindo por este número.'
                                }
                            >
                                {routedFromDepartments.length > 0 ? (
                                    <div className="rounded-lg border border-base-300 bg-base-200/50 px-4 py-3 text-sm text-base-content/70 sm:max-w-md">
                                        Este setor já atende as mensagens de{' '}
                                        <span className="font-medium text-base-content">
                                            {routedFromDepartments
                                                .map(d => d.name)
                                                .join(', ')}
                                        </span>
                                        . Um setor que é destino de roteamento não
                                        pode rotear as mensagens dele.
                                    </div>
                                ) : (
                                    <select
                                        id="sector-route-to"
                                        className="select select-bordered w-full sm:max-w-md"
                                        value={formData.routeToDepartmentId}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                routeToDepartmentId:
                                                    e.target.value,
                                            })
                                        }
                                    >
                                        <option value="">
                                            Este próprio setor atende
                                        </option>
                                        {routeTargets.map(d => (
                                            <option key={d.id} value={d.id}>
                                                {d.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </FormField>

                            {isWeb && isEditing && (
                                <FormField
                                    label="Limite de envios automáticos"
                                    htmlFor="sector-flow-limit-hour"
                                    icon={<MdSpeed className="text-base" />}
                                    optional
                                    hint="Mensagens de automações, fluxos e follow-ups por este número. O volume em rajada é o que bloqueia número no WhatsApp Web. Vazio usa o padrão: 30 por hora e 200 por dia."
                                >
                                    <div className="grid grid-cols-1 gap-3 sm:max-w-md sm:grid-cols-2">
                                        <label className="form-control">
                                            <span className="label-text mb-1.5 text-xs text-base-content/60">
                                                Por hora
                                            </span>
                                            <input
                                                id="sector-flow-limit-hour"
                                                type="number"
                                                min={1}
                                                max={1000}
                                                placeholder="30"
                                                className="input input-bordered w-full"
                                                value={formData.flowSendLimitPerHour}
                                                onChange={e =>
                                                    setFormData({
                                                        ...formData,
                                                        flowSendLimitPerHour:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                        <label className="form-control">
                                            <span className="label-text mb-1.5 text-xs text-base-content/60">
                                                Por dia
                                            </span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={10000}
                                                placeholder="200"
                                                className="input input-bordered w-full"
                                                value={formData.flowSendLimitPerDay}
                                                onChange={e =>
                                                    setFormData({
                                                        ...formData,
                                                        flowSendLimitPerDay:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                    </div>
                                </FormField>
                            )}
                        </FormSection>
                    )}

                    {activeTab === 'membros' && (
                        <FormSection
                            icon={<MdGroup className="text-lg" />}
                            title="Quem enxerga este setor"
                            subtitle="Somente os membros veem os atendimentos que chegam aqui"
                        >
                            {canEditMembers && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="text-sm font-medium">
                                            Adicionar membros
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm gap-2"
                                            disabled={
                                                savingMembers ||
                                                selectedUserIds.length === 0
                                            }
                                            onClick={addSelectedMembers}
                                        >
                                            {savingMembers && (
                                                <span className="loading loading-spinner loading-xs" />
                                            )}
                                            Adicionar
                                            {selectedUserIds.length > 0
                                                ? ` (${selectedUserIds.length})`
                                                : ''}
                                        </button>
                                    </div>

                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Buscar por nome, e-mail ou filial..."
                                        value={userSearch}
                                        onChange={e => setUserSearch(e.target.value)}
                                    />

                                    {availableUsers.length === 0 ? (
                                        <div className="rounded-xl border border-dashed border-base-300 px-4 py-6 text-center text-sm text-base-content/50">
                                            {eligibleUsers.length === 0
                                                ? 'Não foi possível carregar os usuários'
                                                : userSearch
                                                  ? 'Nenhum usuário encontrado'
                                                  : 'Todos os usuários já são membros'}
                                        </div>
                                    ) : (
                                        <div className="max-h-72 overflow-y-auto rounded-lg border border-base-300">
                                            <table className="table table-sm">
                                                <thead className="sticky top-0 z-10 bg-base-100">
                                                    <tr>
                                                        <th className="w-10">
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-sm checkbox-secondary"
                                                                checked={
                                                                    availableUsers.length > 0 &&
                                                                    selectedUserIds.length ===
                                                                        availableUsers.length
                                                                }
                                                                onChange={toggleAllUsers}
                                                            />
                                                        </th>
                                                        <th>Usuário</th>
                                                        <th>Filiais</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {availableUsers.map(candidate => (
                                                        <tr
                                                            key={candidate.id}
                                                            className="cursor-pointer"
                                                            onClick={() =>
                                                                toggleUser(candidate.id)
                                                            }
                                                        >
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="checkbox checkbox-sm checkbox-secondary"
                                                                    checked={selectedUserIds.includes(
                                                                        candidate.id
                                                                    )}
                                                                    onChange={() =>
                                                                        toggleUser(candidate.id)
                                                                    }
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="font-medium">
                                                                    {candidate.name}
                                                                </div>
                                                                {candidate.email && (
                                                                    <div className="text-xs text-base-content/60">
                                                                        {candidate.email}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {candidate.companyNames.length >
                                                                0 ? (
                                                                    <span className="text-xs text-base-content/70">
                                                                        {candidate.companyNames.join(
                                                                            ', '
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge badge-sm badge-ghost">
                                                                        Sem filial
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="divider my-0 text-xs text-base-content/40">
                                Membros do setor
                                {members.length > 0 ? ` (${members.length})` : ''}
                            </div>

                            {loadingMembers ? (
                                <div className="flex justify-center py-10">
                                    <span className="loading loading-spinner loading-md text-secondary" />
                                </div>
                            ) : members.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-base-300 px-4 py-10 text-center">
                                    <MdGroup className="mx-auto mb-2 text-3xl text-base-content/20" />
                                    <p className="text-sm text-base-content/60">
                                        Nenhum membro cadastrado
                                    </p>
                                    <p className="mt-1 text-xs text-base-content/40">
                                        Enquanto estiver assim, os atendimentos
                                        deste setor não aparecem para ninguém.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-hidden rounded-xl border border-base-300">
                                    {members.map(member => (
                                        <div
                                            key={member.id}
                                            className="flex items-center justify-between gap-3 border-b border-base-300/50 px-4 py-3 last:border-b-0"
                                        >
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium">
                                                    {member.user.name}
                                                </p>
                                                <p className="truncate text-xs text-base-content/50">
                                                    {member.user.email ||
                                                        'Sem e-mail'}
                                                </p>
                                            </div>
                                            {canEditMembers && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm btn-circle text-error"
                                                    title="Remover do setor"
                                                    disabled={savingMembers}
                                                    onClick={() =>
                                                        removeMember(
                                                            member.userId
                                                        )
                                                    }
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!canEditMembers && (
                                <p className="text-xs leading-relaxed text-base-content/50">
                                    Você não tem permissão para editar setores,
                                    então esta lista é apenas leitura.
                                </p>
                            )}
                        </FormSection>
                    )}

                    {activeTab === 'ignorados' && id && (
                        <FormSection
                            icon={<MdPersonOff className="text-lg" />}
                            title="Contatos que a IA não responde"
                            subtitle="Parceiros, representantes e quem escreve sem querer agendar nada"
                        >
                            <IgnoredContactsTab
                                departmentId={parseInt(id, 10)}
                                canEdit={canEditMembers}
                            />
                        </FormSection>
                    )}

                    {/* Ações do cadastro. Ficam fora das abas porque o Salvar
                        grava "Dados do setor" e "WhatsApp" juntos — deixá-lo
                        dentro de uma sugeriria que a outra fica de fora.
                        Em "Membros" some: lá cada alteração já grava sozinha, e
                        um Salvar inerte convidaria a esperar por ele. */}
                    <div
                        className={`flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4 sm:flex-row sm:items-center sm:justify-between ${
                            activeTab === 'membros' ||
                            activeTab === 'ignorados'
                                ? 'hidden'
                                : 'flex'
                        }`}
                    >
                        <p className="text-xs text-base-content/50">
                            As duas seções são salvas juntas.
                        </p>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row">
                            <button
                                type="button"
                                className="btn btn-ghost sm:w-36"
                                onClick={() => navigate('/atendimento/sectors')}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-secondary sm:w-36"
                                disabled={
                                    loading ||
                                    !formData.name.trim() ||
                                    !formData.description.trim()
                                }
                            >
                                {loading ? (
                                    <span className="loading loading-spinner"></span>
                                ) : isEditing ? (
                                    'Atualizar'
                                ) : (
                                    'Salvar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {showChannelSwitchModal && (
                <dialog className="modal modal-open">
                    <div className="modal-box max-w-lg">
                        <h3 className="font-bold text-lg">
                            Mudar para a API oficial
                        </h3>
                        <p className="text-sm text-base-content/70 mt-1">
                            O que muda neste setor ao sair do WhatsApp Web:
                        </p>

                        <ul className="mt-3 space-y-2 text-sm">
                            <li className="flex gap-2">
                                <span className="text-warning">•</span>
                                <span>
                                    A sessão pareada por QR é{' '}
                                    <strong>encerrada</strong>. Para voltar ao
                                    WhatsApp Web será preciso parear de novo.
                                </span>
                            </li>
                            <li className="flex gap-2">
                                <span className="text-warning">•</span>
                                <span>
                                    A <strong>janela de 24h</strong> volta a
                                    valer: fora dela, só template aprovado
                                    reabre a conversa.
                                </span>
                            </li>
                            <li className="flex gap-2">
                                <span className="text-warning">•</span>
                                <span>
                                    Apagar e responder mensagem deixam de estar
                                    disponíveis no atendimento.
                                </span>
                            </li>
                        </ul>

                        {loadingScopes ? (
                            <div className="mt-4 flex items-center gap-2 text-sm text-base-content/60">
                                <span className="loading loading-spinner loading-xs" />
                                Verificando notificações...
                            </div>
                        ) : missingScopes.length > 0 ? (
                            <div className="alert alert-warning mt-4 items-start">
                                <div className="text-sm">
                                    <p className="font-semibold">
                                        {missingScopes.length} notificação(ões)
                                        vão parar de ser enviadas
                                    </p>
                                    <p className="opacity-80 mt-0.5">
                                        Não têm template aprovado pela Meta. No
                                        WhatsApp Web funcionavam com o texto
                                        cadastrado.
                                    </p>
                                    <ul className="mt-2 list-disc list-inside space-y-0.5">
                                        {missingScopes.map(scope => (
                                            <li key={scope}>
                                                {TEMPLATE_USAGE_SCOPES.find(
                                                    s => s.value === scope
                                                )?.label ?? scope}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <div className="alert alert-success mt-4 text-sm">
                                Todas as notificações têm template aprovado.
                            </div>
                        )}

                        <label className="flex flex-col gap-1.5 mt-4">
                            <span className="text-sm font-medium text-base-content/80">
                                Número na API oficial
                            </span>
                            <input
                                type="text"
                                className="input input-bordered input-sm"
                                placeholder="(11) 90000-0000"
                                value={officialApiNumber}
                                onChange={e =>
                                    setOfficialApiNumber(
                                        formatPhoneNumber(e.target.value)
                                    )
                                }
                            />
                            <span className="text-xs text-base-content/50">
                                O número atual veio do pareamento por QR e não
                                vale aqui. Informe o número habilitado na conta
                                da API oficial.
                            </span>
                        </label>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowChannelSwitchModal(false)}
                                disabled={savingChannel}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-warning btn-sm"
                                onClick={confirmChannelSwitch}
                                disabled={savingChannel}
                            >
                                {savingChannel && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                Mudar canal
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() =>
                            !savingChannel && setShowChannelSwitchModal(false)
                        }
                    />
                </dialog>
            )}
        </div>
    )
}
